import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { accountsApi } from '@/services/api';
import { Mail, Plus, RefreshCw, Trash2, AlertCircle, CheckCircle2, Loader2, Clock } from 'lucide-react';
import { cn } from '@/utils/cn';
import { ScanActivity } from '@/components/accounts/ScanActivity';
import { BankConnections } from '@/components/accounts/BankConnections';

export function AccountsPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list(),
  });

  const { data: syncStatusData } = useQuery({
    queryKey: ['accounts-sync-status'],
    queryFn: () => accountsApi.syncStatus(),
    refetchInterval: 15000,
  });

  const syncMutation = useMutation({
    mutationFn: (id: string) => accountsApi.sync(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts'] }),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => accountsApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts'] }),
  });

  const [isConnectingEmail, setIsConnectingEmail] = useState(false);

  const accounts = data?.data || [];
  const emailAccounts = accounts.filter((a: any) => a.provider === 'gmail');

  const connectEmail = async () => {
    setIsConnectingEmail(true);
    try {
      const response = await accountsApi.getEmailAuthUrl();
      window.location.href = response.data.url;
    } catch {
      setIsConnectingEmail(false);
    }
  };

  const syncStatus = syncStatusData?.data;
  const syncAccounts = syncStatus?.accounts || [];

  // Build a map of accountId -> sync info for the AccountRow
  const syncInfoMap = new Map<string, any>();
  for (const sa of syncAccounts) {
    syncInfoMap.set(sa.id, sa);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Connected Accounts</h1>
        <p className="text-sm text-muted-foreground">The inboxes and banks Tally reads</p>
      </div>

      {syncStatus && <SyncStatusBanner overall={syncStatus.overall} />}

      <div className="space-y-6">
        <section className="rounded-xl border border-border bg-card p-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100 dark:bg-red-950">
                <Mail className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <h2 className="font-semibold">Email Accounts</h2>
                <p className="text-xs text-muted-foreground">Gmail accounts scanned for receipts and confirmations</p>
              </div>
            </div>
            <button
              onClick={connectEmail}
              disabled={isConnectingEmail}
              className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm font-medium hover:bg-accent disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" /> {isConnectingEmail ? 'Connecting...' : 'Add Email'}
            </button>
          </div>

          <div className="space-y-2">
            {emailAccounts.map((account: any) => (
              <AccountRow
                key={account.id}
                account={account}
                syncInfo={syncInfoMap.get(account.id)}
                icon={<Mail className="h-4 w-4" />}
                onSync={() => syncMutation.mutate(account.id)}
                onRemove={() => removeMutation.mutate(account.id)}
                isSyncing={syncMutation.isPending}
              />
            ))}
            {emailAccounts.map((account: any) => (
              <ScanActivity key={`scan-${account.id}`} accountId={account.id} label={account.institutionName} />
            ))}
            {emailAccounts.length === 0 && (
              <p className="text-sm text-muted-foreground">Your email was connected during sign-in. Add more email accounts to scan additional inboxes.</p>
            )}
          </div>
        </section>

        <BankConnections />
      </div>

    </div>
  );
}

function AccountRow({ account, syncInfo, icon, onSync, onRemove, isSyncing }: {
  account: any;
  syncInfo?: any;
  icon: React.ReactNode;
  onSync: () => void;
  onRemove: () => void;
  isSyncing: boolean;
}) {
  const effectiveStatus = syncInfo?.status || account.status;
  const lastError = syncInfo?.lastError || account.lastError;

  return (
    <div className={cn(
      'rounded-lg border p-3',
      effectiveStatus === 'error' ? 'border-destructive/40 bg-destructive/5' : 'border-border'
    )}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="text-muted-foreground">{icon}</div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium">{account.institutionName}</p>
              {account.accountMask && <span className="text-xs text-muted-foreground">****{account.accountMask}</span>}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              {effectiveStatus === 'syncing' ? (
                <span className="flex items-center gap-1 text-xs text-primary">
                  <Loader2 className="h-3 w-3 animate-spin" /> Syncing...
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {account.lastSyncAt ? formatRelativeTime(new Date(account.lastSyncAt)) : 'Never synced'}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusIcon status={effectiveStatus} />
          <button
            onClick={onSync}
            disabled={isSyncing}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
            title="Sync now"
          >
            <RefreshCw className={cn('h-4 w-4', isSyncing && 'animate-spin')} />
          </button>
          <button
            onClick={onRemove}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            title="Remove"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      {effectiveStatus === 'error' && lastError && (
        <div className="mt-2 ml-7 rounded-md bg-destructive/10 px-3 py-2">
          <p className="text-xs text-destructive">{lastError}</p>
        </div>
      )}
    </div>
  );
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'active') return <CheckCircle2 className="h-4 w-4 text-green-500" />;
  if (status === 'error' || status === 'expired') return <AlertCircle className="h-4 w-4 text-destructive" />;
  if (status === 'syncing') return <RefreshCw className="h-4 w-4 animate-spin text-primary" />;
  return null;
}

function SyncStatusBanner({ overall }: { overall: { totalAccounts: number; pendingJobs: number; activeJobs: number; hasErrors: boolean; isSyncing: boolean } }) {
  if (overall.isSyncing || overall.pendingJobs > 0) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-5 py-4">
        <Loader2 className="h-5 w-5 animate-spin text-primary shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium">Sync in progress</p>
          <p className="text-xs text-muted-foreground">
            {overall.activeJobs > 0 ? `${overall.activeJobs} active` : ''}
            {overall.activeJobs > 0 && overall.pendingJobs > 0 ? ', ' : ''}
            {overall.pendingJobs > 0 ? `${overall.pendingJobs} pending` : ''}
            {' '}sync job{(overall.activeJobs + overall.pendingJobs) !== 1 ? 's' : ''}
          </p>
        </div>
      </div>
    );
  }

  if (overall.hasErrors) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-5 py-4">
        <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-destructive">Some accounts need attention</p>
          <p className="text-xs text-muted-foreground">One or more accounts have sync errors. Check individual accounts below for details.</p>
        </div>
      </div>
    );
  }

  if (overall.totalAccounts > 0) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-green-500/30 bg-green-500/5 px-5 py-4">
        <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium">All accounts synced</p>
          <p className="text-xs text-muted-foreground">{overall.totalAccounts} account{overall.totalAccounts !== 1 ? 's' : ''} connected and up to date</p>
        </div>
      </div>
    );
  }

  return null;
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  if (diffHr < 24) return `${diffHr} hour${diffHr !== 1 ? 's' : ''} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}
